import { useEffect, useRef, useState } from "react";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const INPUT = "h-11 bg-white";
const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-warmgrey">{label}</span>
    {children}
  </label>
);

export const ShowroomBranding = ({ refreshKey = 0, onSaved }) => {
  const [b, setB] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const logoUrl = b?.has_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/dealer/branding/logo?v=${encodeURIComponent(b.logo_version || "")}` : null;

  const load = () => api.get("/dealer/branding").then((r) => {
    setB(r.data);
    setForm({ display_name: r.data.display_name_source === "showroom" ? r.data.display_name : "", cs_phone: r.data.cs_phone, cs_email: r.data.cs_email,
      ...Object.fromEntries(["line1", "line2", "city", "state", "postal_code"].map((k) => [k, r.data.public_address?.[k] || ""])) });
  }).catch(() => setB(null));
  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => { const h = () => load(); window.addEventListener("dealer:profile-changed", h); return () => window.removeEventListener("dealer:profile-changed", h); }, []);

  if (!b || !form) return null;
  const fail = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { display_name: form.display_name, cs_phone: form.cs_phone, cs_email: form.cs_email || undefined };
      if (form.line1 && form.city) payload.public_address = { line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal_code: form.postal_code, country: "US" };
      await api.patch("/dealer/branding", payload);
      toast.success("Showroom branding saved");
      await load();
      onSaved?.();
    } catch (err) { fail(err); } finally { setSaving(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setSaving(true);
    try { await api.post("/dealer/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo updated"); await load(); onSaved?.(); } catch (err) { fail(err); } finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try { await api.delete("/dealer/branding/logo"); toast.success("Logo removed — business name will be used"); await load(); onSaved?.(); } catch (err) { fail(err); } finally { setSaving(false); }
  };

  return (
    <section className="rounded-lg border border-sand bg-white p-4 sm:p-6" data-testid="showroom-branding">
      {b.legal_identity && (
        <div className="mb-4 rounded-md border border-limestone bg-limestone/30 p-3 text-xs" data-testid="legal-identity">
          <p className="text-[11px] font-medium uppercase tracking-wide text-warmgrey">Legal business identity (billing & compliance · managed by Zelligio)</p>
          <p className="mt-1 text-charcoal" data-testid="legal-identity-name"><span className="font-medium">{b.legal_identity.legal_name}</span>{b.legal_identity.dba && b.legal_identity.dba !== b.legal_identity.legal_name ? <span className="text-warmgrey"> · DBA {b.legal_identity.dba}</span> : null}</p>
          {b.legal_identity.legal_address?.line1 && <p className="text-warmgrey">{b.legal_identity.legal_address.line1}, {b.legal_identity.legal_address.city} {b.legal_identity.legal_address.state} {b.legal_identity.legal_address.postal_code}</p>}
          {b.legal_identity.tax_id && <p className="text-warmgrey">Tax ID on file</p>}
        </div>
      )}
      <h2 className="text-base font-medium text-charcoal md:text-lg">Showroom branding</h2>
      <p className="mt-1 text-xs text-warmgrey">Public identity printed on client packing slips for direct-to-client shipments. Legal entity, billing, resale certificate and compliance records are managed by Zelligio and are not editable here.</p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-40 items-center justify-center overflow-hidden rounded-md border border-dashed border-sand bg-limestone/30" data-testid="branding-logo-preview">
          {logoUrl ? <img src={logoUrl} alt="Showroom logo" className="max-h-full max-w-full object-contain" /> : (
            <span className="px-2 text-center font-display text-sm text-charcoal" data-testid="branding-logo-fallback">{b.display_name}</span>
          )}
        </div>
        {b.can_edit && (
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" className="sr-only" id="branding-logo-input" data-testid="branding-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
            <Button type="button" variant="outline" disabled={saving} onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload-btn">
              <Upload className="mr-1 h-4 w-4" /> {b.has_logo ? "Replace logo" : "Upload logo"}
            </Button>
            {b.has_logo && (
              <Button type="button" variant="ghost" disabled={saving} onClick={remove} data-testid="branding-logo-remove-btn"><Trash2 className="mr-1 h-4 w-4" /> Remove</Button>
            )}
          </div>
        )}
        {!b.has_logo && <p className="flex items-center gap-1 text-xs text-warmgrey"><ImageIcon className="h-3 w-3" /> No logo — slips use your showroom name. Orders are never blocked.</p>}
      </div>

      {b.can_edit ? (
        <form onSubmit={save} className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Public showroom name"><Input className={INPUT} value={form.display_name} placeholder={b.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="branding-display-name" /></Field>
          <Field label={`Customer-service phone${b.cs_phone_source === "primary_contact" && b.cs_phone ? " (prefilled from your business contact)" : ""}`}><Input className={INPUT} value={form.cs_phone} onChange={(e) => setForm({ ...form, cs_phone: e.target.value })} data-testid="branding-cs-phone" /></Field>
          <Field label={`Customer-service e-mail${b.cs_email_source === "primary_contact" && b.cs_email ? " (prefilled — changing it never changes your login e-mail)" : ""}`}><Input className={INPUT} type="email" value={form.cs_email} onChange={(e) => setForm({ ...form, cs_email: e.target.value })} data-testid="branding-cs-email" /></Field>
          <Field label={`Public address line 1${b.public_address_source === "default_location" ? " (prefilled from your primary showroom)" : ""}`}><Input className={INPUT} value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} data-testid="branding-addr-line1" /></Field>
          <Field label="Address line 2"><Input className={INPUT} value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} data-testid="branding-addr-line2" /></Field>
          <Field label="City"><Input className={INPUT} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="branding-addr-city" /></Field>
          <Field label="State"><Input className={INPUT} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="branding-addr-state" /></Field>
          <Field label="ZIP"><Input className={INPUT} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="branding-addr-zip" /></Field>
          <div className="sm:col-span-2"><Button type="submit" disabled={saving} className="h-11 bg-terracotta text-white hover:bg-terracotta-hover" data-testid="branding-save">Save showroom branding</Button></div>
        </form>
      ) : (
        <p className="mt-3 text-xs text-warmgrey" data-testid="branding-readonly">Only a Dealer Admin can change showroom branding.</p>
      )}
    </section>
  );
};
